import { generatePath, type Routes } from '@faceup/router'
import { useContext, useMemo } from 'react'
import { LanguageContext } from '../Contexts/LanguageContext'
import { UserContext } from '../Contexts/UserProvider'
import { NoShortIdError } from '../errors/NoShortIdError'

export const routes: Record<keyof RoutesInterface, string> = {
  home: `:language`,
  report: `:language/c/:shortId/report`,
  survey: `:language/q/:shortId`,
  createReport: `:language/c/:shortId/create`,
  checkReport: `:language/c/:shortId/check`,
  checkReportForm: `:language/c/:shortId/check/form`,
  sentReport: `:language/c/:shortId/sent`,
  sentSurvey: `:language/q/:shortId/sent`,
  page: `:language/c/:shortId/page/:id?`,
  settingsBiometrics: `:language/settings/biometrics`,
  shortIdCompany: `:language/c/:shortId/check`,
  shortIdSchool: `:language/s/:shortId/check`,
}

export const useRoutes = (): Routes => {
  const { basename: language } = useContext(LanguageContext)
  const { reportSource } = useContext(UserContext)

  // biome-ignore lint/correctness/useExhaustiveDependencies: Window.location.pathname is not a reactive dependency
  const shortId = useMemo(() => {
    if (reportSource?.shortLink) {
      return reportSource.shortLink
    }
    const pattern = /\/(?:q|c|s)\/([a-zA-Z0-9_]+)/
    const match = window.location.pathname.match(pattern)
    if (match?.[1]) {
      return match[1]
    }
    return null
  }, [window.location.pathname, reportSource?.shortLink])

  // Function definition copied from react-router
  const generatePathWithShortId = <Path extends string>(
    originalPath: Path,
    // biome-ignore lint/suspicious/noExplicitAny: Matches react-router generatePath signature for dynamic params
    params?: any
  ) => {
    if (!shortId && !params?.shortId) {
      throw new NoShortIdError(originalPath)
    }
    const { shortId: _shortId, ...restParams } = params ?? {}
    return generatePath(originalPath, { shortId: _shortId ?? shortId, ...(restParams ?? {}) })
  }

  return {
    home: () => generatePath(routes.home, { language }),
    report: () => generatePathWithShortId(routes.report, { language }),
    survey: () => generatePathWithShortId(routes.survey, { language, shortId }),
    createReport: () => generatePathWithShortId(routes.createReport, { language }),
    checkReport: () => generatePathWithShortId(routes.checkReport, { language }),
    checkReportForm: () => generatePathWithShortId(routes.checkReportForm, { language, shortId }),
    sentReport: () => generatePathWithShortId(routes.sentReport, { language }),
    sentSurvey: () => generatePathWithShortId(routes.sentSurvey, { language }),
    page: ({ id, shortId } = {}) => generatePathWithShortId(routes.page, { language, id, shortId }),
    settingsBiometrics: () => generatePath(routes.settingsBiometrics, { language }),
    shortIdCompany: (shortId: string) => generatePath(routes.shortIdCompany, { language, shortId }),
    shortIdSchool: (shortId: string) => generatePath(routes.shortIdSchool, { language, shortId }),
  }
}

interface RoutesInterface {
  home: () => string
  report: () => string
  survey: () => string
  createReport: () => string
  checkReport: () => string
  checkReportForm: () => string
  sentReport: () => string
  sentSurvey: () => string
  page: (params?: { shortId?: string; id?: string }) => string
  settingsBiometrics: () => string
  shortIdCompany: (shortId: string) => string
  shortIdSchool: (shortId: string) => string
}

declare module '@faceup/router' {
  export interface RoutesOverride extends RoutesInterface {}
}
